import { useState, useRef } from 'react'
import { FaBold, FaItalic, FaUnderline, FaImage } from 'react-icons/fa'
import { BoldExtension, ItalicExtension, UnderlineExtension, ImageExtension, LinkExtension } from 'remirror/extensions'
import { Remirror, useRemirror, EditorComponent, useActive, useCommands } from '@remirror/react'
import { Stack, Box, Icon, Button } from '@/components/lib'
import api from '../../api'

const richContainerSx = {
  whiteSpace: 'pre-wrap',
  img: { display: 'block', maxWidth: '96%', margin: '20px auto' },
  a: {
    color: 'var(--chakra-colors-primary-fg)',
    textDecoration: 'underline',
    _dark: { color: 'var(--chakra-colors-primary-400)' }
  },
  '.remirror-editor': {
    padding: '5px',
    background: 'var(--chakra-colors-bg-surface)',
    color: 'var(--chakra-colors-fg-default)'
  }
}

const Toolbar = ({ forType, forId, allowImages }) => {
  const { bold, italic, underline } = useActive()
  const { toggleBold, toggleItalic, toggleUnderline, insertImage } = useCommands()
  const inputRef = useRef()
  const [uploading, setUploading] = useState(false)

  const handleFileChosen = (e) => {
    const file = e.target.files && e.target.files[0]
    if (file) {
      setUploading(true)
      api.uploads.generateFileUploadRequest({
        forType, forId, name: file.name, size: file.size, type: file.type
      }, (response) => {
        const xhr = new window.XMLHttpRequest()
        xhr.open('PUT', response.signedRequest)
        xhr.setRequestHeader('Content-Type', file.type)
        xhr.onreadystatechange = () => {
          if (xhr.readyState === 4) {
            setUploading(false)
            if (xhr.status === 200) {
              insertImage({ src: response.expectedUrl })
            } else {
              setUploading(false)
            }
          }
        }
        xhr.send(file)
      }, (err) => {
        setUploading(false)
        window.alert('Error uploading file: ' + err.message)
      })
    }
  }

  return (
    <Stack direction='row' alignItems='center' spacing={1} bg='bg.subtle' p={2} borderBottomWidth='1px' borderColor='border.default'>
      <Button size='xs' onClick={() => toggleBold()} variant={bold() ? 'solid' : 'outline'}><Icon as={FaBold} /></Button>
      <Button size='xs' onClick={() => toggleItalic()} variant={italic() ? 'solid' : 'outline'}><Icon as={FaItalic} /></Button>
      <Button size='xs' onClick={() => toggleUnderline()} variant={underline() ? 'solid' : 'outline'}><Icon as={FaUnderline} /></Button>
      {allowImages &&
        <Box pl={5}>
          <Button size='xs' isLoading={uploading} onClick={() => inputRef.current.click()} variant='outline'><Icon as={FaImage} /></Button>
        </Box>}
      <input type='file' style={{ display: 'none' }} ref={inputRef} onChange={handleFileChosen} accept='image/*' />
    </Stack>
  )
}

const Editor = ({ value, onChange, forType, forId, allowImages }) => {
  const { manager, state } = useRemirror({
    extensions: () => [new BoldExtension(), new ItalicExtension(), new UnderlineExtension(), new ImageExtension(), new LinkExtension({ autoLink: true })],
    content: value,
    stringHandler: 'html',
    selection: 'end'
  })

  const onUpdate = d => {
    const html = d.helpers?.getHTML()
    onChange(html)
  }

  return (
    <Box borderColor='border.default' borderWidth='1px' rounded='md' overflow='hidden'>
      <Remirror manager={manager} initialContent={state} onChange={onUpdate}>
        <Toolbar forType={forType} forId={forId} allowImages={allowImages} />
        <Box bg='bg.surface' p={2} sx={richContainerSx}>
          <EditorComponent />
        </Box>
      </Remirror>
    </Box>
  )
}

export default Editor
